/**
 * @module public/js/services/dialers/systemPhoneDialer.js
 * @summary Native phone-app dialer implementation for smartphone Focus Mode sessions.
 */

import BaseDialer from './baseDialer.js';
import { auth } from '../../core/firebase-service.js';

/**
 * Build a native phone deep link for the supplied destination number.
 *
 * @param {object} [options={}] - Call launch options.
 * @param {string} [options.to] - Destination phone number in E.164 format.
 * @param {string} [options.extension] - Optional phone extension.
 * @returns {string} Native phone deep link.
 */
export function buildSystemPhoneDeeplink({ to = '', extension = '' } = {}) {
  const base = `tel:${String(to || '').trim()}`;
  const ext = String(extension || '').replace(/[^0-9A-Da-d*#]/g, '');
  return ext ? `${base};ext=${encodeURIComponent(ext.toUpperCase())}` : base;
}

export default class SystemPhoneDialer extends BaseDialer {
  /**
   * @param {{ apiClient:Object, endpoints:Object, windowObject?:Window|{location?:{assign?:Function,href?:string}} }} deps
   */
  constructor({
    apiClient,
    endpoints,
    windowObject = typeof window !== 'undefined' ? window : undefined,
  } = {}) {
    super();
    if (!apiClient || !endpoints) {
      throw new Error('SystemPhoneDialer needs { apiClient, endpoints }');
    }
    this.apiClient = apiClient;
    this.endpoints = endpoints;
    this.windowObject = windowObject;
  }

  /**
   * Launch the device dialer after seeding a backend call-intent record.
   *
   * @param {object} opts
   * @param {string} opts.to - E.164 dial target.
   * @param {string=} opts.extension - Optional phone extension.
   * @param {string=} opts.contactId - CRM contact identifier.
   * @param {string=} opts.userId - Explicit user identifier.
   * @param {string=} opts.taskId - Focus Mode task identifier.
   * @returns {Promise<{ callSid: string|null }>} Call intent identifier.
   */
  async call({
    to,
    extension = '',
    contactId = '',
    userId = '',
    taskId = '',
  } = {}) {
    if (!to) throw new Error('SystemPhoneDialer.call → missing `to` number');

    const uid =
      userId ||
      auth?.currentUser?.uid ||
      this.apiClient?.auth?.currentUser?.uid ||
      window?.di?.auth?.currentUser?.uid ||
      null;

    const tenantId =
      auth?.currentUser?.tenantId ||
      this.apiClient?.auth?.currentUser?.tenantId ||
      window?.APP_CONFIG?.tenantId ||
      window?.di?.auth?.currentUser?.tenantId ||
      null;

    const payload = {
      toNumber: to,
      extension: extension || undefined,
      contactId: contactId || undefined,
      userId: uid || undefined,
      taskId: taskId || undefined,
    };
    if (tenantId) payload.tenantId = tenantId;

    let callSid = null;
    try {
      const url =
        typeof this.endpoints.systemCallInitiate === 'function'
          ? this.endpoints.systemCallInitiate({})
          : this.endpoints.systemCallInitiate;
      const res = await this.apiClient.fetchJson(url, {
        auth: true,
        body: payload,
      });
      if (res?.ok === false) {
        throw new Error(
          res?.error || 'SystemPhoneDialer: backend reported failure'
        );
      }
      callSid =
        res?.callSid ||
        res?.data?.callSid ||
        res?.data?.sid ||
        res?.sid ||
        null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[SystemPhoneDialer] intent logging failed', err);
    }

    const deeplink = buildSystemPhoneDeeplink({ to, extension });
    if (typeof this.windowObject?.location?.assign === 'function') {
      this.windowObject.location.assign(deeplink);
    } else if (this.windowObject?.location) {
      this.windowObject.location.href = deeplink;
    }

    return { callSid };
  }
}
